import { useState, useCallback } from "react";
import type { CopyNurses } from "../Roster_create";

interface HistoryEntry {
  nurses: CopyNurses;
  modifiedCells: Set<string>; // 해당 시점의 수정된 셀 스냅샷
}

interface UseRosterHistoryParams {
  copyNurses: CopyNurses;
  setCopyNurses: React.Dispatch<React.SetStateAction<CopyNurses>>;
  addModifiedCell?: (nurseId: string, dayIndex: number) => void;
  modifiedCells: Set<string>;
  setModifiedCells: React.Dispatch<React.SetStateAction<Set<string>>>;
}

type RosterShiftEdit = {
  readonly nurseId: string;
  readonly scheduleDay: number;
  readonly changeShift: string;
  readonly cellShiftDbId?: number | null;
};

export function applyRosterShiftEdit(
  nurses: CopyNurses,
  edit: RosterShiftEdit
): CopyNurses {
  return nurses.map((nurse) => {
    if (String(nurse.nurse_id) !== edit.nurseId) return nurse;

    const sched = nurse.schedule ?? [];
    const nextSchedule = sched.map((shift: string | null, idx: number) =>
      idx === edit.scheduleDay ? edit.changeShift : shift
    );

    const rawIds = (nurse as { schedule_ids?: (number | null)[] | null })
      .schedule_ids;
    const len = Math.max(nextSchedule.length, rawIds?.length ?? 0);
    const nextIds: (number | null)[] = Array.from(
      { length: len },
      (_, index) => rawIds?.[index] ?? null
    );

    const trimmed = edit.changeShift.trim();
    const blank = !trimmed || trimmed === "-";
    if (edit.scheduleDay >= 0 && edit.scheduleDay < len) {
      if (blank) {
        nextIds[edit.scheduleDay] = null;
      } else if (
        edit.cellShiftDbId != null &&
        Number.isFinite(Number(edit.cellShiftDbId))
      ) {
        nextIds[edit.scheduleDay] = Number(edit.cellShiftDbId);
      } else {
        nextIds[edit.scheduleDay] = rawIds?.[edit.scheduleDay] ?? null;
      }
    }

    return {
      ...nurse,
      schedule: nextSchedule,
      schedule_ids: nextIds,
    };
  });
}

export default function useRosterHistory({
  copyNurses,
  setCopyNurses,
  addModifiedCell,
  modifiedCells,
  setModifiedCells,
}: UseRosterHistoryParams) {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [future, setFuture] = useState<HistoryEntry[]>([]);

  const saveHistory = useCallback(() => {
    setHistory((prev) => [
      ...prev,
      {
        nurses: copyNurses,
        modifiedCells: new Set(modifiedCells),
      },
    ]);
    setFuture([]);
  }, [copyNurses, modifiedCells]);

  const handleUndo = useCallback(() => {
    if (history.length === 0) return;
    const previous = history[history.length - 1];
    const newHistory = history.slice(0, -1);

    setFuture((prev) => [
      {
        nurses: copyNurses,
        modifiedCells: new Set(modifiedCells),
      },
      ...prev,
    ]);
    setCopyNurses(previous.nurses);
    setHistory(newHistory);
    setModifiedCells(new Set(previous.modifiedCells));
  }, [
    history,
    copyNurses,
    modifiedCells,
    setCopyNurses,
    setModifiedCells,
  ]);

  const handleRedo = useCallback(() => {
    if (future.length === 0) return;
    const next = future[0];
    const newFuture = future.slice(1);

    setHistory((prev) => [
      ...prev,
      {
        nurses: copyNurses,
        modifiedCells: new Set(modifiedCells),
      },
    ]);
    setCopyNurses(next.nurses);
    setFuture(newFuture);
    setModifiedCells(new Set(next.modifiedCells));
  }, [
    future,
    copyNurses,
    modifiedCells,
    setCopyNurses,
    setModifiedCells,
  ]);

  // 근무코드 수정 (동일 shift_id가 여러 행일 때 통계·해석은 schedule_ids와 맞춤)
  const modDayShift = useCallback(
    (
      nurseId: string,
      scheduleDay: number,
      changeShift: string,
      cellShiftDbId?: number | null
    ) => {
      setCopyNurses((prev) =>
        applyRosterShiftEdit(prev, {
          nurseId,
          scheduleDay,
          changeShift,
          cellShiftDbId,
        })
      );
      addModifiedCell?.(nurseId, scheduleDay);
      setTimeout(() => {
        saveHistory();
      }, 0);
    },
    [setCopyNurses, saveHistory, addModifiedCell]
  );

  const deleteAllShift = useCallback(() => {
    setCopyNurses((prev) =>
      prev.map((nurse) => {
        const sched = nurse.schedule
          ? new Array(nurse.schedule.length).fill("-")
          : [];
        const schedule_ids = sched.map(() => null);
        return {
          ...nurse,
          schedule: sched,
          schedule_ids,
        };
      })
    );
    setTimeout(() => {
      saveHistory();
    }, 0);
  }, [setCopyNurses, saveHistory]);

  const clearHistory = useCallback(() => {
    setHistory([]);
    setFuture([]);
    setModifiedCells(new Set());
  }, [setModifiedCells]);

  return {
    saveHistory,
    handleUndo,
    handleRedo,
    modDayShift,
    deleteAllShift,
    canUndo: history.length > 0,
    canRedo: future.length > 0,
    clearHistory,
  };
}
